import { z } from "zod";
import type { EnterpriseLocale } from "../shared/locale";

const MAX_CHAT_HISTORY_MESSAGES = 20;

const conversationIdSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/);

export const predefinedQuestionIdSchema = z.enum([
  "enterprise_value",
  "focus_today",
  "biggest_win",
  "missed_items",
]);

export const clarificationOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
});

const clarificationBaseSchema = z.object({
  id: z.string().min(1),
  question: z.string().min(1),
});

export const activeClarificationSchema = z.union([
  clarificationBaseSchema.extend({
    mode: z.enum(["select", "multi_select"]),
    options: z.array(clarificationOptionSchema).min(1),
  }),
  clarificationBaseSchema.extend({
    mode: z.literal("free_text"),
    options: z.array(clarificationOptionSchema).default([]),
  }),
  clarificationBaseSchema.extend({
    mode: z.literal("select").optional(),
    options: z.array(clarificationOptionSchema).min(1),
  }),
]);

export const clarificationSelectionSchema = z.discriminatedUnion("mode", [
  z.object({
    clarificationId: z.string().min(1),
    question: z.string().min(1),
    mode: z.literal("select"),
    option: clarificationOptionSchema,
  }),
  z.object({
    clarificationId: z.string().min(1),
    question: z.string().min(1),
    mode: z.literal("multi_select"),
    options: z.array(clarificationOptionSchema).min(1),
  }),
  z.object({
    clarificationId: z.string().min(1),
    question: z.string().min(1),
    mode: z.literal("free_text"),
    response: z.string().min(1),
  }),
]);

export const chatHistoryMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  clarification: activeClarificationSchema.nullable().optional(),
});

export const chatMessageBodySchema = z.object({
  conversationId: conversationIdSchema,
  message: z.string().min(1).max(2000),
  predefinedQuestionId: predefinedQuestionIdSchema.optional(),
  clarificationSelection: clarificationSelectionSchema.optional(),
  activeClarification: activeClarificationSchema.optional(),
  history: z.preprocess(
    (history) =>
      Array.isArray(history)
        ? history.slice(-MAX_CHAT_HISTORY_MESSAGES)
        : history,
    z.array(chatHistoryMessageSchema).default([]),
  ),
});

export const chatConversationListQuerySchema = z.object({
  cursor: z.string().min(1).max(2048).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(50),
});

export const chatConversationSummarySchema = z.object({
  conversationId: conversationIdSchema,
  title: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }).nullable(),
  updatedAt: z.string().datetime({ offset: true }).nullable(),
});

export const chatConversationListResponseSchema = z.object({
  conversations: z.array(chatConversationSummarySchema),
  nextCursor: z.string().nullable(),
});

export const chatConversationHistoryResponseSchema = z.object({
  conversationId: conversationIdSchema,
  messages: z.array(chatHistoryMessageSchema),
});

export type EnterpriseChatRequest = z.infer<typeof chatMessageBodySchema> & {
  customerId: number;
  locale?: EnterpriseLocale;
};

export const fixItVisualizationPointSchema = z.object({
  label: z.string(),
  value: z.number(),
});

export const fixItVisualizationSchema = z.object({
  type: z.enum(["bar", "line", "table"]),
  title: z.string(),
  xLabel: z.string().nullable(),
  yLabel: z.string().nullable(),
  points: z.array(fixItVisualizationPointSchema),
});

export const chatResponseSchema = z.object({
  answer: z.string(),
  rowCount: z.number(),
  visualization: fixItVisualizationSchema.nullable(),
  clarification: activeClarificationSchema.nullable().optional(),
});

export type EnterprisePredefinedQuestionId = z.infer<
  typeof predefinedQuestionIdSchema
>;
export type EnterpriseClarificationOption = z.infer<
  typeof clarificationOptionSchema
>;
export type EnterpriseActiveClarification = z.infer<
  typeof activeClarificationSchema
>;
export type EnterpriseClarificationSelection = z.infer<
  typeof clarificationSelectionSchema
>;
export type EnterpriseChatHistoryMessage = z.infer<
  typeof chatHistoryMessageSchema
>;
export type EnterpriseChatConversationSummary = z.infer<
  typeof chatConversationSummarySchema
>;
export type EnterpriseChatConversationListResponse = z.infer<
  typeof chatConversationListResponseSchema
>;
export type EnterpriseChatConversationHistoryResponse = z.infer<
  typeof chatConversationHistoryResponseSchema
>;
